import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Col,
  Row,
  Tabs,
  Tooltip,
  Typography,
} from "../utils/antd-imports/AntdImports";
import { useDispatch, useSelector } from "react-redux";
import ChatMsgPad from "../chat-communication/ChatMsgPad";
import "./CampaignsChatList.css";
import { capitalizeFirst, truncateDescriptionText } from "../utils/Utils";
import { markProductAsRead } from "../../../store/slices/common-slice/chat-communication/ChatCommunicationActionItems";
import { clearLiveMsgNotificationForProduct } from "../../../store/slices/common-slice/web-sockets/WebSocketMsgSlice";
import { getChatHistory } from "../../../store/thunks/common-thunks/commonThunk";

const ProductChatList = ({ products }) => {
  const { Text, Title } = Typography;
  const dispatch = useDispatch();

  /* REDUX STATE */
  const { isLiveMsgNotification } = useSelector((state) => state.webSocketMsgs);
  const { clearedCampaigns, clearedProducts } = useSelector(
    (state) => state.chatCommunicationHandleSlice
  );

  /* ACTIVE TAB STATE */
  const [activeKey, setActiveKey] = useState(null);

  /* LIVE PRODUCT MESSAGE STATS */
  const productLiveStats = useMemo(() => {
    if (!products?.campaignId) return {};

    return isLiveMsgNotification.reduce((acc, msg) => {
      const campaignId = String(msg.campaignId);
      const bookingId = String(msg.bookingId);

      if (campaignId !== String(products.campaignId)) return acc;

      const msgTime = new Date(msg.timestamp).getTime();
      const clearedCampaignTime = clearedCampaigns?.[campaignId]
        ? new Date(clearedCampaigns[campaignId]).getTime()
        : 0;
      if (msgTime <= clearedCampaignTime) return acc;

      const clearedProductTime = clearedProducts?.[campaignId]?.[bookingId]
        ? new Date(clearedProducts[campaignId][bookingId]).getTime()
        : 0;
      if (msgTime <= clearedProductTime) return acc;

      // ✅ SKIP counting messages for the active product
      if (bookingId === String(activeKey)) return acc;

      if (!acc[bookingId]) {
        acc[bookingId] = {
          count: 0,
          lastMessageTime: msgTime,
        };
      }

      acc[bookingId].count += 1;
      acc[bookingId].lastMessageTime = Math.max(
        acc[bookingId].lastMessageTime,
        msgTime
      );

      return acc;
    }, {});
  }, [
    isLiveMsgNotification,
    products?.campaignId,
    clearedCampaigns,
    clearedProducts,
    activeKey, // important!
  ]);

  /* MERGE PRODUCTS WITH LIVE COUNTS */
  const mergedProducts = useMemo(() => {
    return (
      products?.products?.map((product) => {
        const bookingId = String(product.bookingId);
        const live = productLiveStats[bookingId];

        return {
          ...product,
          messageCount: live?.count ?? 0,
          lastMessageTime: live?.lastMessageTime ?? 0,
        };
      }) || []
    );
  }, [products?.products, productLiveStats]);

  /* INITIAL ACTIVE TAB */
  useEffect(() => {
    if (!activeKey && mergedProducts.length) {
      setActiveKey(mergedProducts[0].bookingId);
    }
  }, [mergedProducts, activeKey]);

  /* LOAD CHAT + MARK AS READ ON TAB CHANGE */
  useEffect(() => {
    if (!activeKey || !products?.campaignId) return;

    const campaignId = String(products.campaignId);
    const bookingId = String(activeKey);

    dispatch(
      getChatHistory({
        campaignId,
        bookingId,
      })
    );

    // Mark active product as read
    dispatch(
      markProductAsRead({
        campaignId,
        productId: bookingId,
        readAt: new Date().toISOString(),
      })
    );

    // Optional: clear live notifications for this product
    dispatch(
      clearLiveMsgNotificationForProduct({
        campaignId,
        productId: bookingId,
      })
    );
  }, [activeKey, products?.campaignId, dispatch]);
  useEffect(() => {
    if (!activeKey || !products?.campaignId) return;

    const campaignId = String(products.campaignId);
    const bookingId = String(activeKey);

    const hasActiveLiveMsg = isLiveMsgNotification.some(
      (msg) =>
        String(msg.campaignId) === campaignId &&
        String(msg.bookingId) === bookingId
    );

    if (!hasActiveLiveMsg) return;

    // 1️⃣ mark as read
    dispatch(
      markProductAsRead({
        campaignId,
        productId: bookingId,
        readAt: Date.now(),
      })
    );

    // 2️⃣ remove from live notifications
    dispatch(
      clearLiveMsgNotificationForProduct({
        campaignId,
        productId: bookingId,
      })
    );
  }, [isLiveMsgNotification, activeKey, products?.campaignId, dispatch]);

  /* TAB ITEMS */
  const tabItems = mergedProducts.map((item) => {
    const isActive = item.bookingId === activeKey;

    return {
      key: item.bookingId,
      label: (
        <div style={{ width: 280 }}>
          <Row>
            <Col span={16}>
              <Tooltip title={item.title}>
                <Text className="chat-product-name">
                  {capitalizeFirst(truncateDescriptionText(item.title))}
                </Text>
              </Tooltip>
              <br />
              <Text className="product-buyer-name">
                Buyer: {capitalizeFirst(item.user_full_name)}
              </Text>
            </Col>

            <Col
              span={8}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {/* ACTIVE TAB SHOULD NEVER SHOW COUNT */}
              <Badge
                count={isActive ? 0 : item.messageCount}
                style={{ backgroundColor: "#d2042d" }}
              />
            </Col>
          </Row>
        </div>
      ),
    };
  });

  /* ACTIVE PRODUCT */
  const activeProduct = mergedProducts.find((p) => p.bookingId === activeKey);

  /* RENDER */
  return (
    <div>
      <div style={{ padding: 16 }}>
        <Title className="chat-campaign-name">
          {capitalizeFirst(products?.campaignName)}
        </Title>
        <Text className="select-prodcut-sub-text">
          Select a product to view buyer conversations
        </Text>
      </div>

      <div
        style={{
          display: "flex",
          height: "calc(90vh - 120px)",
          borderTop: "1px solid #e5e5e5",
        }}
      >
        <div
          className="chat-container-tabs"
          style={{
            width: 320,
            borderRight: "1px solid #e5e5e5",
            background: "#fff",
          }}
        >
          <Tabs
            items={tabItems}
            activeKey={activeKey}
            onChange={setActiveKey}
            tabPosition="left"
            tabBarGutter={24}
            className="filter-tab"
          />
        </div>

        <div
          style={{
            flex: 1,
            padding: 24,
            background: "#fff",
          }}
        >
          {activeProduct ? (
            <ChatMsgPad campaignId={products.campaignId} />
          ) : (
            <Text>Select a product to view messages</Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductChatList;
